import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import moment from "moment";

import { getCartera } from "../helpers/rutaCarteras";

const fmtARS = (n) =>
  Number(n || 0).toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const money = (n) => `$ ${fmtARS(n)}`;

const estadoUi = (v) => {
  const s = String(v || "").toUpperCase().trim();
  return s === "ENDOSADO_A_INGRESAR" ? "EMITIDO_A_INGRESAR" : s;
};

const tipoUi = (v) => String(v || "").toUpperCase().trim();

const fecha = (v) => {
  if (!v) return "";
  const m = moment(v);
  return m.isValid() ? m.format("DD/MM/YYYY") : "";
};

const getNro = (r) => r?.nro || r?.nroCheque || r?.idICheq || "";
const getBanco = (r) => r?.bancoEmisor || r?.banco || "";

const getFechaCheque = (r) => r?.fechaCheque || r?.fechaPagoDiferido || "";

const getFechaPago = (r) =>
  r?.fechaPago || r?.fechaLiquidacion || r?.fechaPagoDiferido || "";

const getCobradoA = (r) => r?.cobradoA || r?.librador || r?.emisor || "";
const getPagadoA = (r) => r?.pagadoA || r?.beneficiario || "";

const getCliente = (r) =>
  r?.clienteId?.razonsocial || r?.clienteId?.nombre || "";

const getOrigen = (r) => {
  if (r?.ocId?.numero) return `OC ${r.ocId.numero}`;
  if (r?.ocId?.nroOrdenCliente) return `OC ${r.ocId.nroOrdenCliente}`;

  if (r?.opId?.numero) return `OP ${r.opId.numero}`;
  if (r?.opId?.nroOrdenPago) return `OP ${r.opId.nroOrdenPago}`;

  if (r?.origenTipo) return r.origenTipo;
  return "";
};

const sumImporte = (arr) =>
  arr.reduce((acc, x) => acc + Number(x?.importe || 0), 0);

const byTipo = (arr, tipo) =>
  arr.filter((x) => tipoUi(x.tipo) === tipo);

const isEnCartera = (r) => estadoUi(r.estado) === "EN_CARTERA";

const isEmitido = (r) =>
  ["EMITIDO", "EMITIDO_A_INGRESAR"].includes(estadoUi(r.estado));

const isAnulado = (r) => estadoUi(r.estado) === "ANULADO";

const sortByVencimiento = (a, b) => {
  const fa = getFechaPago(a) || getFechaCheque(a) || a?.recibidoEn;
  const fb = getFechaPago(b) || getFechaCheque(b) || b?.recibidoEn;

  return moment(fa || 0).valueOf() - moment(fb || 0).valueOf();
};

const diasHastaVencimiento = (r) => {
  const f = getFechaPago(r) || getFechaCheque(r);
  if (!f) return null;

  const hoy = moment().startOf("day");
  const venc = moment(f).startOf("day");

  if (!venc.isValid()) return null;

  return venc.diff(hoy, "days");
};

const dentroDeProximosDias = (r, dias) => {
  const d = diasHastaVencimiento(r);
  return d !== null && d >= 0 && d <= Number(dias || 7);
};

function TotalCard({ titulo, valor, subtitulo, variante = "azul", formato = "money" }) {
  const color =
    variante === "rojo" ? "#dc3545" :
    variante === "verde" ? "#198754" :
    variante === "negro" ? "#212529" :
    "#0d6efd";

  const bg =
    variante === "rojo" ? "#fff5f5" :
    variante === "verde" ? "#f2fff7" :
    variante === "negro" ? "#f8f9fa" :
    "#f8fbff";

  const valorMostrar =
    formato === "percent"
      ? `${Number(valor || 0).toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} %`
      : formato === "veces"
      ? `${Number(valor || 0).toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} veces`
      : money(valor);

  return (
    <Card
      className="border-0 shadow-sm h-100"
      style={{
        borderLeft: `5px solid ${color}`,
        background: bg,
      }}
    >
      <Card.Body className="py-3" style={{ textAlign: "left" }}>
        <div
          className="text-muted"
          style={{
            fontSize: 13,
            fontWeight: 600,
            textTransform: "uppercase",
          }}
        >
          {titulo}
        </div>

        <div
          className="fw-bold"
          style={{
            fontSize: 28,
            lineHeight: 1.1,
            color,
            letterSpacing: "-0.5px",
          }}
        >
          {valorMostrar}
        </div>

        {subtitulo ? (
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            {subtitulo}
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
}
function labelDias(d) {
  if (d === null) return "";
  if (d === 0) return "HOY";
  if (d === 1) return "MAÑANA";
  return `${d} días`;
}

function TablaVencimientos({ titulo, items, tipoInforme }) {
  const total = sumImporte(items);

  return (
    <Card className="mt-3 shadow-sm border-0">
      <Card.Body>
        <div
          className="d-flex flex-wrap align-items-center mb-2"
          style={{ gap: 12 }}
        >
          <h5 className="mb-0">{titulo}</h5>

          <span
            style={{
              background: tipoInforme === "pagar" ? "#fff5f5" : "#f2fff7",
              color: tipoInforme === "pagar" ? "#dc3545" : "#198754",
              border:
                tipoInforme === "pagar"
                  ? "1px solid #f1aeb5"
                  : "1px solid #a3cfbb",
              borderRadius: 6,
              padding: "4px 10px",
              fontSize: 15,
              fontWeight: 700,
            }}
          >
            Total: {money(total)}
          </span>
        </div>

        <div className="table-responsive" style={{ maxHeight: 430, overflow: "auto" }}>
          <Table
            striped
            bordered
            hover
            size="sm"
            className="mb-0"
            style={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                <th>Tipo</th>
                <th>Estado</th>
                <th style={{ textAlign: "right" }}>Importe</th>
                <th>Fecha pago</th>
                <th>Días</th>
                <th>Nro</th>
                <th>Banco</th>
                <th>Cobrado a</th>
                <th>Pagado a</th>
                <th>Cliente</th>
                <th>Origen</th>
                <th>Observaciones</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center">
                    Sin registros.
                  </td>
                </tr>
              ) : (
                items.map((r) => {
                  const dias = diasHastaVencimiento(r);
                  const urgente = dias !== null && dias <= 2;

                  return (
                    <tr
                      key={r._id || `${getNro(r)}-${r.tipo}-${r.estado}`}
                      style={
                        urgente
                          ? {
                              background: "#ffe5e5",
                              color: "#b42318",
                              fontWeight: 600,
                            }
                          : {}
                      }
                    >
                      <td>{tipoUi(r.tipo)}</td>
                      <td>{estadoUi(r.estado)}</td>
                      <td style={{ textAlign: "right" }}>{money(r.importe)}</td>
                      <td>{fecha(getFechaPago(r))}</td>
                      <td>{labelDias(dias)}</td>
                      <td>{getNro(r)}</td>
                      <td>{getBanco(r)}</td>
                      <td>{getCobradoA(r)}</td>
                      <td>{getPagadoA(r)}</td>
                      <td>{getCliente(r)}</td>
                      <td>{getOrigen(r)}</td>
                      <td>{r?.observaciones || ""}</td>
                    </tr>
                  );
                })
              )}
            </tbody>

            <tfoot>
              <tr
                style={{
                  background: tipoInforme === "pagar" ? "#fff5f5" : "#f2fff7",
                  color: tipoInforme === "pagar" ? "#dc3545" : "#198754",
                  fontWeight: "bold",
                }}
              >
                <td colSpan={2}>
                  <strong>Total</strong>
                </td>
                <td style={{ textAlign: "right", fontSize: 14 }}>
                  <strong>{money(total)}</strong>
                </td>
                <td colSpan={9}></td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function InformeVencimientosChequesECheq() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [dias, setDias] = useState(7);

  const consultar = async () => {
    setErr("");
    setLoading(true);

    try {
      const resp = await getCartera();

      const items = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.items)
        ? resp.items
        : Array.isArray(resp?.elementos)
        ? resp.elementos
        : [];

      setData(
        items.map((x) => ({
          ...x,
          estado: estadoUi(x.estado),
          tipo: tipoUi(x.tipo),
        }))
      );
    } catch (e) {
      setData([]);
      setErr(e?.message || "No se pudo cargar el informe.");
    } finally {
      setLoading(false);
    }
  };

  const grupos = useMemo(() => {
    const base = data
      .filter((x) => !isAnulado(x))
      .filter((x) => dentroDeProximosDias(x, dias));

    const aCobrar = base.filter(isEnCartera).sort(sortByVencimiento);
    const aPagar = base.filter(isEmitido).sort(sortByVencimiento);

    return {
      aCobrar,
      aPagar,

      chequesACobrar: byTipo(aCobrar, "CHEQUE"),
      echeqACobrar: byTipo(aCobrar, "ICHEQ"),

      chequesAPagar: byTipo(aPagar, "CHEQUE"),
      echeqAPagar: byTipo(aPagar, "ICHEQ"),
    };
  }, [data, dias]);

  const totalACobrar = sumImporte(grupos.aCobrar);
  const totalAPagar = sumImporte(grupos.aPagar);
  const diferencia = totalACobrar - totalAPagar;
  const coberturaPorcentaje =
    totalAPagar > 0 ? (totalACobrar / totalAPagar) * 100 : 0;

  const coberturaVeces =
    totalAPagar > 0 ? totalACobrar / totalAPagar : 0;

  return (
    <Container fluid className="py-3">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-3">
            <div>
              <h4 className="mb-1">
                Vencimientos próximos - Cheques / eCheq
              </h4>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Control de cobros y obligaciones próximas
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap align-items-center">
                <Form.Select
                size="sm"
                value={dias}
                onChange={(e) => setDias(Number(e.target.value))}
                style={{ width: 200 }}
                >
                <option value={2}>Próximos 2 días</option>
                <option value={5}>Próximos 5 días</option>
                <option value={7}>Próximos 7 días</option>
                <option value={10}>Próximos 10 días</option>
                <option value={15}>Próximos 15 días</option>
                <option value={30}>Próximos 30 días</option>
                </Form.Select>

              <Button onClick={consultar} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" /> Consultando…
                  </>
                ) : (
                  "Consultar"
                )}
              </Button>
            </div>
          </div>

          {err ? (
            <Alert className="mt-3 mb-0" variant="danger">
              {err}
            </Alert>
          ) : null}
        </Card.Body>
      </Card>

      {data.length === 0 ? (
        <div className="mt-3 text-muted">
          Tocá <b>Consultar</b> para cargar el informe.
        </div>
      ) : (
        <>
          <Row className="mt-3 g-2">
            <Col md={3}>
              <TotalCard
                titulo={`A cobrar próximos ${dias} días`}
                valor={totalACobrar}
                subtitulo={`${grupos.aCobrar.length} registros`}
                variante="verde"
              />
            </Col>

            <Col md={3}>
              <TotalCard
                titulo={`A pagar próximos ${dias} días`}
                valor={totalAPagar}
                subtitulo={`${grupos.aPagar.length} registros`}
                variante="rojo"
              />
            </Col>

            <Col md={3}>
              <TotalCard
                titulo="Diferencia cobrar - pagar"
                valor={diferencia}
                subtitulo={diferencia >= 0 ? "Cubre obligaciones" : "Faltante financiero"}
                variante={diferencia >= 0 ? "verde" : "rojo"}
              />
            </Col>

            <Col md={3}>
                <TotalCard
                    titulo="Cobertura"
                    valor={coberturaVeces}
                    formato="veces"
                    subtitulo={`${coberturaPorcentaje.toLocaleString("es-AR", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                    })}% de cobertura`}
                    variante={coberturaPorcentaje >= 100 ? "verde" : "rojo"}
                />
            </Col>
          </Row>

          <Card className="mt-3 shadow-sm border-0">
            <Card.Body>
              <h5 className="mb-3">Resumen próximos {dias} días</h5>

              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Grupo</th>
                      <th style={{ textAlign: "right" }}>Cantidad</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td><strong>CHEQUES A COBRAR</strong></td>
                      <td style={{ textAlign: "right" }}>{grupos.chequesACobrar.length}</td>
                      <td style={{ textAlign: "right" }}>{money(sumImporte(grupos.chequesACobrar))}</td>
                    </tr>

                    <tr>
                      <td><strong>ECHEQ A COBRAR</strong></td>
                      <td style={{ textAlign: "right" }}>{grupos.echeqACobrar.length}</td>
                      <td style={{ textAlign: "right" }}>{money(sumImporte(grupos.echeqACobrar))}</td>
                    </tr>

                    <tr>
                      <td><strong>CHEQUES A PAGAR</strong></td>
                      <td style={{ textAlign: "right" }}>{grupos.chequesAPagar.length}</td>
                      <td style={{ textAlign: "right" }}>{money(sumImporte(grupos.chequesAPagar))}</td>
                    </tr>

                    <tr>
                      <td><strong>ECHEQ A PAGAR</strong></td>
                      <td style={{ textAlign: "right" }}>{grupos.echeqAPagar.length}</td>
                      <td style={{ textAlign: "right" }}>{money(sumImporte(grupos.echeqAPagar))}</td>
                    </tr>

                    <tr
                      style={{
                        background: diferencia >= 0 ? "#f2fff7" : "#fff5f5",
                        color: diferencia >= 0 ? "#198754" : "#dc3545",
                        fontWeight: "bold",
                      }}
                    >
                      <td><strong>DIFERENCIA</strong></td>
                      <td style={{ textAlign: "right" }}></td>
                      <td style={{ textAlign: "right" }}>{money(diferencia)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <TablaVencimientos
            titulo="CHEQUES A COBRAR"
            items={grupos.chequesACobrar}
            tipoInforme="cobrar"
          />

          <TablaVencimientos
            titulo="ECHEQ A COBRAR"
            items={grupos.echeqACobrar}
            tipoInforme="cobrar"
          />

          <TablaVencimientos
            titulo="CHEQUES A PAGAR"
            items={grupos.chequesAPagar}
            tipoInforme="pagar"
          />

          <TablaVencimientos
            titulo="ECHEQ A PAGAR"
            items={grupos.echeqAPagar}
            tipoInforme="pagar"
          />
        </>
      )}
    </Container>
  );
}